import React from 'react';

import { DateFormatPatterns } from '@clearverify/constants';
import type { Nullable } from '@clearverify/types';
import { formatDate } from '@clearverify/utils';

import { EmployerSimpleInfoFragment, QueryMode, useEmployersSimpleListQuery } from 'src/api';
import { Table, TableColumn, TableThemes } from 'src/components/table';
import { usePagination, usePaginationQueryVariables } from 'src/hooks/common';

import { PeoEmployersLiveStatusTableNameCell } from './PeoEmployersLiveStatusTableNameCell';
import { PeoEmployersLiveStatusTableSwitchCell } from './PeoEmployersLiveStatusTableRow';

type PeoEmployersLiveStatusTableProps = {
  peoId: string;
};

/** Needs a `PaginationProvider` above it. */
export const PeoEmployersLiveStatusTable: React.FC<PeoEmployersLiveStatusTableProps> = ({
  peoId,
}) => {
  const [searchValue, setSearchValue] = React.useState<Nullable<string>>();
  const pagination = usePagination();
  const { first, skip } = usePaginationQueryVariables();

  const { data, loading } = useEmployersSimpleListQuery({
    variables: {
      filter: {
        peoId: { equals: peoId },
        displayName: searchValue
          ? { contains: searchValue, mode: QueryMode.Insensitive }
          : undefined,
      },
      first,
      skip,
    },
  });

  const handleSearch = React.useCallback(
    (value: Nullable<string>) => {
      setSearchValue(value);
      pagination.resetPage();
    },
    [pagination],
  );

  return (
    <Table<EmployerSimpleInfoFragment>
      dataSource={data?.employerList ?? []}
      rowCount={data?.employerCount}
      columns={TABLE_COLUMNS}
      loading={loading}
      pagination={pagination}
      onSearch={handleSearch}
      searchLabel="Employer Display Name"
      tableTheme={TableThemes.withBorder}
    />
  );
};

const TABLE_COLUMNS: TableColumn<EmployerSimpleInfoFragment>[] = [
  {
    key: 'displayName',
    title: 'Name',
    render: (_, employer) => <PeoEmployersLiveStatusTableNameCell employer={employer} />,
  },
  {
    key: 'setLiveAt',
    title: 'Set Live At',
    render: (_, employer) =>
      employer.setLiveAt
        ? formatDate(employer.setLiveAt, DateFormatPatterns.shortDateWithSlash)
        : '-',
  },
  {
    key: 'switch',
    title: '',
    width: 90,
    render: (_, employer) => <PeoEmployersLiveStatusTableSwitchCell employer={employer} />,
  },
];
